/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useCallback, useEffect, useState } from 'react';
import StatAttribute from './StatAttribute'
import TaperedRule from './TaperedRule';
import PropertySet from './PropertySet';
import EditableText from './EditableText';
import AbilitiesBlock from './AbilitiesBlock';
import MultiSelectString from './MultiSelectString';
import SpeedSelector from './SpeedSelector';
import SensesSelector from './SensesSelector';
import SavingThrowSelector from './SavingThrowSelector';
import './MonsterDisplay.css';
import DraggableImage from './DraggableImage';
import RaceSelector from './RaceSelector';
import ArmorSelector from './ArmorSelector';
import HitPointSelector from './HitPointSelector';
import ChallengeRatingSelector from './ChallengeRatingSelector';
import Utils from './model/Utils';
import Monster from './model/Monster';
import SkillsSelector from './SkillsSelector';
import SkillSet from './model/SkillSet';
import DataModel from './model/DataModel';
import Armor from './model/Armor';
import StatAttributeMm2024 from './StatAttributeMm2024';
import PropertySetDouble from './PropertySetDouble';
import Attributes from './model/Attributes';
import AttributeValues from './model/AttributeValues';

interface MonsterDisplayProps {
  mon: Monster; // Define a Monster interface with all the properties of the monster
  handleUpdateMonster: (field: string, value: any) => void;
  showAll: boolean;
  useMm2024: boolean;
  exportImageDivSource: React.RefObject<HTMLDivElement>;
  twoColumns: boolean;
  dataModel: DataModel;
  onFileDropped: (file: File) => void;
  onClosePopups: () => void;
  openBlock: string;
  setOpenBlock: (openBlock: string) => void;
  openIndex: number
  setOpenIndex: (openIndex: number) => void;
  searchOpen: boolean;
  setSearchOpen: (searchOpen: boolean) => void;
  spellbookOpen: boolean;
  setSpellbookOpen: (spellbookOpen: boolean) => void;
  onRefreshTour: (tourState: string) => void;
}

const MonsterDisplay: React.FC<MonsterDisplayProps> = ({ mon, handleUpdateMonster, showAll, useMm2024, exportImageDivSource, twoColumns, dataModel, onFileDropped,
                                                         onClosePopups, openBlock, setOpenBlock, openIndex, setOpenIndex, searchOpen, setSearchOpen,
                                                         spellbookOpen, setSpellbookOpen, onRefreshTour }) => {
  const [dragging, setDragging] = useState(false);
  const [, setForceUpdate] = useState(0); // Dummy state to force re-renders

  useEffect(() => {
    // This update just sets a member variable to force a redraw:
    const update = () => setForceUpdate((prev) => prev + 1);
    dataModel.subscribe(update);

    // Cleanup on unmount
    return () => {
      dataModel.subscribe(() => {}); // No-op to prevent stale subscriptions
    };
  }, [dataModel]);

  useEffect(() => {
    mon.skillSet = SkillSet.updateLevels(mon.skillSet, mon.attributes, mon.proficiencyBonus);
  }, [mon.attributes, mon.proficiencyBonus]);

  useEffect(() => {
    onClosePopups();
  }, [openBlock, openIndex]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!dragging) {
      setDragging(true);
    }
  }, [dragging]);

  const handleDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      onFileDropped(file);
      e.dataTransfer.clearData();
    }
  }, [onFileDropped]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenIndex(-1); // Close any dialog that is currently open
      }
    };
    window.document.addEventListener('keydown', handleKeyDown);
    // Remove the event listener on cleanup
    return () => {
      window.document.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Empty dependency array means this effect runs once on mount and cleanup on unmount

  // force each item in each of the lists to have a unique ID
  for (const valueArray of [mon.actions, mon.bonus_actions, mon.reactions, mon.traits, mon.legendary_actions]) {
    let id = 0;
    for (const item of valueArray ?? []) {
      if (item) {
        item.id = id++;
      }
    }
  }
  const showEmptyHeader = showAll ? 1: 0;
  const blockStartAbility      = 0;
  const blockStartActions      = blockStartAbility      + (mon.traits?.length || showEmptyHeader);
  const blockStartSpells       = blockStartActions      + (mon.actions?.length || showEmptyHeader);
  const blockStartBonusActions = blockStartSpells       + (mon.spells?.length || showEmptyHeader);
  const blockStartReaction     = blockStartBonusActions + (mon.bonus_actions?.length || showEmptyHeader);
  const blockStartLegendary    = blockStartReaction     + (mon.reactions?.length || showEmptyHeader);

  let blockStartMythic         = blockStartLegendary    + (mon.legendary_actions?.length || showEmptyHeader);
  if ((mon.legendary_actions?.length > 0) && (showAll || mon.legendary_desc)) blockStartMythic++;

  let blockStartRegional       = blockStartMythic       + (mon.mythic_actions?.length || showEmptyHeader);
  if ((mon.mythic_actions?.length > 0) && (showAll || mon.mythic_desc)) blockStartRegional++;

  let blockStartLair           = blockStartRegional     + (mon.regional_actions?.length || showEmptyHeader);
  if ((mon.regional_actions?.length > 0) && (showAll || mon.regional_desc))     blockStartLair++;
  if ((mon.regional_actions?.length > 0) && (showAll || mon.regional_desc_end)) blockStartLair++;

  let blockStartInventory      = blockStartLair         + (mon.lair_actions?.length || showEmptyHeader);
  if ((mon.lair_actions?.length > 0) && (showAll || mon.lair_desc))     blockStartInventory++;
  if ((mon.lair_actions?.length > 0) && (showAll || mon.lair_desc_end)) blockStartInventory++;

  const blockStartNext           = blockStartInventory    + (mon.inventory?.length || showEmptyHeader);

  if (mon.splitPoint > blockStartNext) {
    mon.splitPoint = blockStartNext;
  }

  type AbilityArrayKeys = { 
    [K in keyof Monster]: Monster[K] extends Ability[] ? K : never 
  }[keyof Monster];
  const sectionOptions = new Map<string, AbilityArrayKeys>();
  
  sectionOptions.set('Traits',            'traits');
  sectionOptions.set('Actions',           'actions');
  sectionOptions.set('Spellcasting',      'spells');
  sectionOptions.set('Bonus Actions',     'bonus_actions');
  sectionOptions.set('Reactions',         'reactions');
  sectionOptions.set('Legendary Actions', 'legendary_actions');
  sectionOptions.set('Mythic',            'mythic_actions');
  sectionOptions.set('Regional Effects',  'regional_actions');
  sectionOptions.set('Lair Actions',      'lair_actions');
  sectionOptions.set('Inventory',         'inventory');
  
  const handleSectionChange = (newSectionName: string, ability: Ability, oldSectionName: string) => {
    if (!ability.name) {
      ability.name = '';
    }
    console.log(`Section ${ability.name} moving from ${oldSectionName} to ${newSectionName}`);
    
    const oldSectionVar = oldSectionName ? sectionOptions.get(oldSectionName) : sectionOptions.get('Abilities');
    const newSectionVar = newSectionName ? sectionOptions.get(newSectionName) : sectionOptions.get('Abilities');
    
    if (newSectionVar && oldSectionVar) {
      // Access the sections in the `Monster` object
      const oldSection = mon[oldSectionVar] as Ability[];
      const newSection = mon[newSectionVar] as Ability[];
      
      // Type guard to ensure both oldSection and newSection are arrays of Ability
      if (Array.isArray(oldSection) && oldSection.every(item => isAbility(item)) &&
          Array.isArray(newSection) && newSection.every(item => isAbility(item))) {
        
        // Find the ability in the old section
        const index = oldSection.indexOf(ability);
        
        if (index > -1) {
          // Remove the ability from the old section
          oldSection.splice(index, 1);

          ability.id = (newSection.reduce((acc: number, curr: Ability) => ((curr.id && (curr.id > acc)) ? curr.id : acc), 0) + 1);

          // Add the ability to the new section
          newSection.push(ability);
          
          // Update the monster object with the modified sections
          mon[oldSectionVar] = oldSection;
          mon[newSectionVar] = newSection;
          setOpenBlock(newSectionName === 'Abilities' ? '' : newSectionName);
          setOpenIndex(newSection.length-1);
        }
      } else {
        console.error("One of the sections is not an array of abilities.");
      }
    }
  };
  
  // Helper function to check if an item is of type Ability
  function isAbility(item: any): item is Ability {
    return item && typeof item.name === 'string'; // Adjust this check to match the structure of an Ability
  }
  
  const abilitysBlockRender = (
    side: 'left' | 'right' | 'all',
    fieldName: Extract<keyof Monster, { [K in keyof Monster]: Monster[K] extends Ability[] ? K : never }[keyof Monster]>,
    name: string,
    start: number,
    preTextFieldName: Extract<keyof Monster, { [K in keyof Monster]: Monster[K] extends string ? K : never }[keyof Monster]> | null,
    postTextFieldName: Extract<keyof Monster, { [K in keyof Monster]: Monster[K] extends string ? K : never }[keyof Monster]> | null,
  ): JSX.Element => {
    return (
      <AbilitiesBlock mon={mon} 
                      openIndex={openBlock === name ? openIndex : -1} 
                      useMm2024={useMm2024}
                      setOpenIndex={(index) => { setOpenBlock(name); setOpenIndex(index); }}
                      onSave={(newValue) => handleUpdateMonster(fieldName, newValue)}
                      onAddLeftItem={() => mon.splitPoint = mon.splitPoint + 1}
                      onNpcUpdate={(newValue) => handleUpdateMonster('npcData', newValue)}
                      alwaysShow={showAll}
                      side={side}
                      start={start}
                      name={name}
                      sectionName={name}
                      sectionOptions={[...sectionOptions.keys()]}
                      onSectionChange={handleSectionChange}
                      blockType={fieldName}
                      values={mon[fieldName] || []}
                      preText={preTextFieldName ? mon[preTextFieldName] : undefined}
                      onPreTextSave={preTextFieldName ? (newValue) => handleUpdateMonster(preTextFieldName, newValue) : undefined}
                      postText={postTextFieldName ? mon[postTextFieldName] : undefined}
                      onPostTextSave={postTextFieldName ? (newValue) =>  handleUpdateMonster(postTextFieldName, newValue) : undefined}
                      searchOpen={searchOpen}
                      setSearchOpen={setSearchOpen}
                      spellbookOpen={spellbookOpen}
                      setSpellbookOpen={setSpellbookOpen}
                      onRefreshTour={onRefreshTour}
                      />
    );
  }
  const abilitiesBlockRender = (side: 'left' | 'right' | 'all'): JSX.Element => {
      return (<>
              {abilitysBlockRender(side, 'traits',            'Traits',             blockStartAbility,      null, null)}
              {abilitysBlockRender(side, 'actions',           'Actions',            blockStartActions,      null, null)}
              {abilitysBlockRender(side, 'spells',            'Spellcasting',       blockStartSpells,       null, null)}
              {abilitysBlockRender(side, 'bonus_actions',     'Bonus Actions',      blockStartBonusActions, null, null)}
              {abilitysBlockRender(side, 'reactions',         'Reactions',          blockStartReaction,     null, null)}
              {abilitysBlockRender(side, 'legendary_actions', 'Legendary Actions',  blockStartLegendary,    'legendary_desc', null)}
              {abilitysBlockRender(side, 'mythic_actions',    'Mythic',             blockStartMythic,       'mythic_desc',    null)}
              {abilitysBlockRender(side, 'regional_actions',  'Regional Effects',   blockStartRegional,     'regional_desc',  'regional_desc_end')}
              {abilitysBlockRender(side, 'lair_actions',      'Lair Actions',       blockStartLair,         'lair_desc',      'lair_desc_end')}
              {abilitysBlockRender(side, 'inventory',         'Inventory',          blockStartInventory,    null, null)}
              </>);
  }

  const handleUpdateArmor = (ac: number, desc: string, armor: Armor | null) => {
    handleUpdateMonster('armor_class', ac);
    handleUpdateMonster('armor_desc', desc);
    handleUpdateMonster('armor', armor);
  }

  const onToggleSavingThrow = (attribute: keyof AttributeValues) => {
    const attrShort = attribute.toUpperCase().substring(0, 3);
    const newSaves: string[] = [];
    let found = false;
    for (const save of mon.saving_throws?.split(/, */) || []) {
      if (save.toUpperCase().startsWith(attrShort)) {
        // If this is present in the saving_throws string, conintuing will remove it, because it won't be pushed into newSaves.
        found = true;
        continue;
      }
      newSaves.push(save);
    }
    if (!found) {
      // If it wasn't found, add it:
      newSaves.push(attribute);
    }
    newSaves.sort((a: string, b: string): number => {
      const findIndex = (attr: string) => {
        for (let i=0 ; i<Attributes.attributes.length ; i++) {
          if (Attributes.attributes[i].toUpperCase().startsWith(attr.toUpperCase().substring(0, 3))) {
            return i;
          }
        }
        return -1;
      }
      const indexA = findIndex(a);
      const indexB = findIndex(b);
      if (indexA == indexB) {
        return 0;
      }
      if (indexA > indexB) {
        return 1;
      }
      return -1;
    });
    handleUpdateMonster('saving_throws', newSaves.filter(o=>o).join(', '));
  }

  const savingThrowLevels = new Map<keyof AttributeValues, number>();
  for (const attr of Attributes.attributes) {
    const attrLevel = mon.attributes[attr];
    let level = Attributes.getBonus(attrLevel);
    savingThrowLevels.set(attr, level);
  }
  for (const save of mon.saving_throws?.toUpperCase().split(/, */) || []) {
    for (const attr of Attributes.attributes) {
      const attrShort = attr.toUpperCase().substring(0, 3);
      if (save.startsWith(attrShort)) {
        let newLevel = savingThrowLevels.get(attr)! + mon.proficiencyBonus;
        savingThrowLevels.set(attr, newLevel);
      }
    }
  }

  const initiative = mon.skillSet?.skills['Initiative']?.adjLevel ?? Attributes.getBonus(mon.attributes.dexterity);
  const image = mon.image;
  const clip = image?.clip && (openIndex === -1);
  return (
    <div id="stat-block-wrapper">
      <div className={'stat-block' + (twoColumns ? ' wide' : '')} style={{position: 'relative'}} ref={exportImageDivSource}
         onDragOver={handleDragOver}
         onDragLeave={handleDragLeave}
         onDrop={handleDrop}>
        <hr className="orange-border"/>
        <div className={'insideBorders' + (clip ? '' : ' no-clip')} style={{position: 'relative', zIndex: 2}}>
          <div id="dragndrop-overlay" className={dragging ? '' : "hidden"}>
              <div>
                  <span className="fa fa-sign-in-alt fa-rotate-90"></span><br/>
                  Drop here
              </div>
          </div>
          {useMm2024 && (<h1 id="monster-name" className="creature-heading">
                <EditableText initialValue={mon.name} onSave={(newValue) => handleUpdateMonster('name', newValue)} alwaysShow={true} />
                  <hr/>
          </h1>)}
          <div className="section-left">
            <div className="creature-heading">
              {!useMm2024 && (<h1 id="monster-name">
                <EditableText initialValue={mon.name} onSave={(newValue) => handleUpdateMonster('name', newValue)} alwaysShow={true} />
              </h1>)}
              <h2 id="monster-type">
                <MultiSelectString values={mon.size} singleSelection isOpen={(openBlock === 'monSize')}
                         setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'monSize' : ''); setOpenIndex(newIsOpen ? 1 : -1)}}
                         options={Utils.sizes} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}
                         onSave={(newValue: string) => handleUpdateMonster('size', newValue)}/>&nbsp;
                <RaceSelector type={mon.type} subtype={mon.subtype} isOpen={(openBlock === 'monType')}
                         setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'monType' : ''); setOpenIndex(newIsOpen ? 1 : -1)}}
                         onSaveType={(newValue) => handleUpdateMonster('type', newValue)}
                         onSaveSubtype={(newValue) => handleUpdateMonster('subtype', newValue)}/>,&nbsp;
                <MultiSelectString values={mon.alignment} singleSelection isOpen={(openBlock === 'monAlign')}
                         setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'monAlign' : ''); setOpenIndex(newIsOpen ? 1 : -1)}}
                         options={Utils.alignments} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}} allowCustom
                         onSave={(newValue: string) => handleUpdateMonster('alignment', newValue)}/>
              </h2>
            </div>
            <TaperedRule />
            <div className="top-stats">
              <div className="property-line first">
                <h4>{useMm2024 ? 'AC' : 'Armor Class'}</h4>
                  <ArmorSelector armorType={mon.armor} armorClass={mon.armor_class} armorDesc={mon.armor_desc} useMm2024={useMm2024}
                                 dexterity={mon.attributes.dexterity} strength={mon.attributes.strength} isOpen={(openBlock === 'armr')} 
                                 setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'armr' : ''); setOpenIndex(newIsOpen ? 1 : -1)}}
                                 onSave={handleUpdateArmor} />
                  {useMm2024 && 
                  <span style={{marginLeft: '35px'}}>
                    <h4>Initiative</h4>
                    <span className='edit-field'
                          onClick={() => {
                            const isOpen = (openBlock === 'skills');
                            setOpenBlock(isOpen ? '' : 'skills');
                            setOpenIndex(isOpen ? -1 : 1);
                          }}>
                      +{initiative} ({10 + initiative})
                    </span>
                  </span>}
              </div>
              <HitPointSelector mon={mon} useMm2024={useMm2024}
                                onSaveHitDiceNumber={(val) => {handleUpdateMonster('hitDiceNumber', val)}}
                                onSaveHitDiceType={(val) => {handleUpdateMonster('hitDiceType', val)}}
                                onSaveHitDiceBonus={(val) => {handleUpdateMonster('hitDiceBonus', val)}}
                                onSaveUsePcHitDice={(val) => {handleUpdateMonster('usePcHitDice', val)}}
                                onSaveHitPointsCustom={(val) => {handleUpdateMonster('hitPointsCustom', val)}}
                                isOpen={(openBlock === 'hp')} 
                                setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'hp' : ''); setOpenIndex(newIsOpen ? 1 : -1)}}/>
              <SpeedSelector speed={mon.speed} isOpen={(openBlock === 'speed')} 
                                 setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'speed' : ''); setOpenIndex(newIsOpen ? 1 : -1)}}
                                 onSave={(newValue) => handleUpdateMonster('speed', newValue)}/> 
              <TaperedRule />
              {useMm2024 ? (
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <table className="attributes">
                          <tbody>
                            <tr className='attribute-header'>
                              <td></td>
                              <td></td>
                              <td>MOD</td>
                              <td>SAVE</td>
                            </tr>
                            <StatAttributeMm2024 label='STR' attrClass='attr-left' modClass='attr-left-mod' value={mon.attributes.strength}     onUpdate={(newValue) => handleUpdateMonster('attributes.strength',     newValue)} onToggleSavingThrow={() => onToggleSavingThrow('strength')}     savingThrow={savingThrowLevels.get('strength')!}/>
                            <StatAttributeMm2024 label='DEX' attrClass='attr-left' modClass='attr-left-mod' value={mon.attributes.dexterity}    onUpdate={(newValue) => handleUpdateMonster('attributes.dexterity',    newValue)} onToggleSavingThrow={() => onToggleSavingThrow('dexterity')}    savingThrow={savingThrowLevels.get('dexterity')!}/>
                            <StatAttributeMm2024 label='CON' attrClass='attr-left' modClass='attr-left-mod' value={mon.attributes.constitution} onUpdate={(newValue) => handleUpdateMonster('attributes.constitution', newValue)} onToggleSavingThrow={() => onToggleSavingThrow('constitution')} savingThrow={savingThrowLevels.get('constitution')!}/>
                          </tbody>
                        </table>
                      </td>
                      <td>
                        <table className="attributes">
                          <tbody>
                            <tr className='attribute-header'>
                              <td></td>
                              <td></td>
                              <td>MOD</td>
                              <td>SAVE</td>
                            </tr>
                            <StatAttributeMm2024 label='INT' attrClass='attr-right' modClass='attr-right-mod' value={mon.attributes.intelligence} onUpdate={(newValue) => handleUpdateMonster('attributes.intelligence', newValue)} onToggleSavingThrow={() => onToggleSavingThrow('intelligence')} savingThrow={savingThrowLevels.get('intelligence')!}/>
                            <StatAttributeMm2024 label='WIS' attrClass='attr-right' modClass='attr-right-mod' value={mon.attributes.wisdom}       onUpdate={(newValue) => handleUpdateMonster('attributes.wisdom',       newValue)} onToggleSavingThrow={() => onToggleSavingThrow('wisdom')}       savingThrow={savingThrowLevels.get('wisdom')!}/>
                            <StatAttributeMm2024 label='CHA' attrClass='attr-right' modClass='attr-right-mod' value={mon.attributes.charisma}     onUpdate={(newValue) => handleUpdateMonster('attributes.charisma',     newValue)} onToggleSavingThrow={() => onToggleSavingThrow('charisma')}     savingThrow={savingThrowLevels.get('charisma')!}/>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div className="attributes">
                    <StatAttribute label="STR" value={mon.attributes.strength}     onUpdate={(newValue) => handleUpdateMonster('attributes.strength',     newValue)} />
                    <StatAttribute label="DEX" value={mon.attributes.dexterity}    onUpdate={(newValue) => handleUpdateMonster('attributes.dexterity',    newValue)} />
                    <StatAttribute label="CON" value={mon.attributes.constitution} onUpdate={(newValue) => handleUpdateMonster('attributes.constitution', newValue)} />
                    <StatAttribute label="INT" value={mon.attributes.intelligence} onUpdate={(newValue) => handleUpdateMonster('attributes.intelligence', newValue)} />
                    <StatAttribute label="WIS" value={mon.attributes.wisdom}       onUpdate={(newValue) => handleUpdateMonster('attributes.wisdom',       newValue)} />
                    <StatAttribute label="CHA" value={mon.attributes.charisma}     onUpdate={(newValue) => handleUpdateMonster('attributes.charisma',     newValue)} />
                </div>
              )}
              <TaperedRule />
              {!useMm2024 && (<SavingThrowSelector values={mon.saving_throws}
                                   isOpen={(openBlock === 'save')} 
                                   setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'save' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} 
                                   attributes={mon.attributes}
                                   proficiency={mon.proficiencyBonus}
                                   alwaysShow={showAll}
                                   onSave={(newValue) => {handleUpdateMonster('saving_throws', newValue)}} />
              )}
              <SkillsSelector alwaysShow={showAll}
                              skillSet={mon.skillSet}
                              useMm2024={useMm2024}
                              isOpen={(openBlock === 'skills')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'skills' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} 
                              attributes={mon.attributes}
                              proficiency={mon.proficiencyBonus}
                              onSave={(newSkillSet) => {handleUpdateMonster('skillSet', newSkillSet);}} />
              <PropertySet className='damage-vulnerabilities' columns={1} isOpen={(openBlock === 'damV')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'damV' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} alwaysShow={showAll} name={useMm2024 ? "Vulnerabilities" : "Damage Vulnerabilities"} allowCustom options={Utils.damageTypes} values={mon.damage_vulnerabilities || ''} onSave={(newValue) => handleUpdateMonster('damage_vulnerabilities', newValue)} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}/>
              <PropertySet className='damage-resistances' columns={1} isOpen={(openBlock === 'damR')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'damR' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} alwaysShow={showAll} name={useMm2024 ? "Resistances" : "Damage Resistances"} allowCustom options={Utils.damageTypes} values={mon.damage_resistances || ''} onSave={(newValue) => handleUpdateMonster('damage_resistances', newValue)} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}/>
              {!useMm2024 && <PropertySet className='damage-immunities' columns={1} isOpen={(openBlock === 'damI')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'damI' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} alwaysShow={showAll} name="Damage Immunities" allowCustom options={Utils.damageTypes} values={mon.damage_immunities || ''} onSave={(newValue) => handleUpdateMonster('damage_immunities', newValue)} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}/>}
              {!useMm2024 && <PropertySet className='condition-immunities' columns={2} isOpen={(openBlock === 'cond')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'cond' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} alwaysShow={showAll} name="Condition Immunities" allowCustom options={Utils.conditions} values={mon.condition_immunities || ''} onSave={(newValue) => handleUpdateMonster('condition_immunities', newValue)} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}/>}
              {useMm2024 && <>
                <PropertySetDouble className='immunities' name="Immunities" alwaysShow={showAll} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}
                        columns1={1} isOpen1={(openBlock === 'damI')} setIsOpen1={(newIsOpen) => {setOpenBlock(newIsOpen ? 'damI' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} allowCustom1 options1={Utils.damageTypes} values1={mon.damage_immunities || ''}    onSave1={(newValue) => handleUpdateMonster('damage_immunities', newValue)}
                        columns2={2} isOpen2={(openBlock === 'cond')} setIsOpen2={(newIsOpen) => {setOpenBlock(newIsOpen ? 'cond' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} allowCustom2 options2={Utils.conditions}  values2={mon.condition_immunities || ''} onSave2={(newValue) => handleUpdateMonster('condition_immunities', newValue)} />
                <PropertySet className='gear' columns={2} isOpen={(openBlock === 'gear')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'gear' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} alwaysShow={showAll} name="Gear" allowCustom options={Utils.gear} values={mon.gear || ''} onSave={(newValue) => handleUpdateMonster('gear', newValue)} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}/>
              </>}
              <SensesSelector isOpen={(openBlock === 'sens')} 
                         setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'sens' : ''); setOpenIndex(newIsOpen ? 1 : -1)}}
                         sensesObj={mon.sensesObject}
                         onSave={(newValue) => handleUpdateMonster('sensesObject', newValue)}
                         onPassivePerceptionChange={(newValue) => handleUpdateMonster('passivePerception', newValue)}
                         passivePerception={mon.passivePerception} />
              <PropertySet className='languages' columns={2} isOpen={(openBlock === 'lang')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'lang' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} alwaysShow allowCustom name="Languages" options={Utils.langs} values={mon.languages || ''} onSave={(newValue) => handleUpdateMonster('languages', newValue)} onClickOutside={() => {setOpenBlock(''); setOpenIndex(-1)}}/>
              <div className="property-line">
                <h4>Challenge</h4>
                <ChallengeRatingSelector useMm2024={useMm2024} isOpen={(openBlock === 'cr')} setIsOpen={(newIsOpen) => {setOpenBlock(newIsOpen ? 'cr' : ''); setOpenIndex(newIsOpen ? 1 : -1)}} challenge_rating={mon.challenge_rating}  onSave={(newValue) => handleUpdateMonster('challenge_rating', newValue)}/>
                {!useMm2024 && (
                  <span className='no-wrap' style={{float: 'right', marginRight: '30px'}}>
                    <h4>Proficiency&nbsp;Bonus</h4>&nbsp;<span className="attributes">+<EditableText width='30px' min={1} max={10} type="number" initialValue={mon.proficiencyBonus} onSave={(newValue) => handleUpdateMonster('proficiencyBonus', parseInt(''+newValue))}/></span>
                  </span>)}
              </div>
            </div>
            <TaperedRule />
            <div className="actions">
              {twoColumns && abilitiesBlockRender('left')}
              {!twoColumns && abilitiesBlockRender('all')}
            </div>
          </div>
          <div className="section-right">
            <div className="actions">
              {twoColumns && abilitiesBlockRender('right')}
            </div>
          </div>
          <div id="image-padding" style={{marginTop: '0px', height: '1px'}}></div>
          {image && (
          <DraggableImage imageSrc={image.src}
                name=''
                width={image.width ?? 400}
                bottom={image.positionBottom ?? 0}
                right={image.positionRight ?? 0}
                zIndex={image.behind ? -1 : 1}
                opacity={image.opacity ?? 1.0}
                onMouseDown={() => {setOpenBlock(''); setOpenIndex(-1)}}
                transform={'rotate(' + (image.rotation || 0) + 'deg)  scaleX(' + (image.horizontalFlip ? -1 : 1) + ')'}
                onChangeXY={(dx, dy) => {handleUpdateMonster('image.positionRight', ((image.positionRight ?? 0) + dx));
                                         handleUpdateMonster('image.positionBottom', ((image.positionBottom ?? 0) + dy));} }
                 />
          )}
          <div style={{height: (10*mon.extraWhitespace) + 'px'}}/>
        </div>
        <hr className="orange-border bottom"/>
      </div>
    </div>
  );
};

export default MonsterDisplay;
